import baseApi from "../utils/baseApi";

const USER_FILE_API = "/user/documents";
const ADMIN_FILE_API = "/admin/document";
const SUPERADMIN_FILE_API = "/superadmin/space-file";

const USER_UPLOAD_FILE_API = '/users/space-file/upload';
const ADMIN_UPLOAD_FILE_API = '/admin/space-file/upload';
const SUPERADMIN_UPLOAD_FILE_API = '/superadmin/space-file/upload';


const userData = JSON.parse(localStorage.getItem("user") || "{}");
const userRole = userData?.role;

const getAPIPath = () => {
    switch (userRole) {
        case "superadmin": return SUPERADMIN_UPLOAD_FILE_API;
        case "admin": return ADMIN_UPLOAD_FILE_API;
        default: return USER_UPLOAD_FILE_API;
    }
};

// Super Admin

export const uploadFileAPI = async (file: File, folderId: string | null, spaceId: string) => {
    try {
        const apiPath = getAPIPath();

        const formData = new FormData();
        formData.append("document", file);

        // Only append folder_id if it is not null
        if (folderId) {
            formData.append("folder_id", folderId);
        }

        formData.append("space_id", spaceId);

        const response = await baseApi.post(`${apiPath}`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });

        return response.data;
    } catch (error) {
        console.error("Upload Error:", error);
        throw new Error("Failed to upload document (super admin).");
    }
};

export const updateFileSuperAdmin = async (id: string, newName: string) => {
    try {
        const response = await baseApi.put(`${SUPERADMIN_FILE_API}/${id}/update-name`, { document_name: newName });
        return response.data;
    } catch (error) {
        throw new Error("Failed to update document (super admin).");
    }
};

export const deleteFileSuperAdmin = async (id: string) => {
    try {
        await baseApi.delete(`${SUPERADMIN_FILE_API}/${id}`);
        return { message: "Document deleted successfully" };
    } catch (error) {
        throw new Error("Failed to delete document (super admin).");
    }
};




// 📌 Admin Methods

// Get all documents (admin)
export const getFiles = async () => {
    try {
        const response = await baseApi.get('/admin/documents');
        return response.data;
    } catch (error) {
        throw new Error("Failed to get documents (admin).");
    }
};

// Get a specific document (admin)
export const getDocument = async (id: string) => {
    try {
        const response = await baseApi.get(`${ADMIN_FILE_API}/${id}`);
        return response.data;
    } catch (error) {
        throw new Error("Failed to get document (admin).");
    }
};

// Upload a document (admin)
export const uploadFileAdmin = async (file: File, folderId: string, userId: string) => {

    try {
        const formData = new FormData();
        formData.append("document", file);
        formData.append("folder_id", folderId);
        formData.append("user_id", userId);

        const response = await baseApi.post("/admin/upload-document", formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });

        return response.data;
    } catch (error) {
        throw new Error("Failed to upload document (admin).");
    }
};

// Update document name (admin)
export const updateDocument = async (id: string, newName: string) => {
    try {
        const response = await baseApi.put(`${ADMIN_FILE_API}/${id}`, { document_name: newName });
        return response.data;
    } catch (error) {
        throw new Error("Failed to update document (admin).");
    }
};

// Delete a document (admin)
export const deleteDocument = async (id: string) => {
    try {
        await baseApi.delete(`${ADMIN_FILE_API}/${id}`);
        return { message: "Document deleted successfully" };
    } catch (error) {
        throw new Error("Failed to delete document (admin).");
    }
};

// 📌 User Methods

// Upload a document (user)
export const uploadFileUser = async (file: File, folderId: string) => {
    try {
        const formData = new FormData();
        formData.append("document", file);
        formData.append("folder_id", folderId);

        const response = await baseApi.post("/user/upload-document", formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });

        return response.data;
    } catch (error) {
        throw new Error("Failed to upload document (user).");
    }
};

// Download folder as ZIP (user)
export const downloadFolderZip = async (userId: string, folderId: string, folderName: string) => {
    try {
        const response = await baseApi.post(
            "/user/downloadZip",
            { user_id: userId, folder_id: folderId },
            { responseType: "blob" }
        );

        if (!response || !response.data) {
            throw new Error("No data received from the server.");
        }

        // Create a Blob URL and trigger download
        const blob = new Blob([response.data], { type: "application/zip" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${folderName}.zip`; // Use folder name as the ZIP file name
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        return { message: "Folder downloaded successfully" };
    } catch (error: any) {
        // Handle API errors based on response status
        if (error.response) {
            if (error.response.status === 404) {
                throw new Error("Folder is empty.");
            } else if (error.response.status === 500) {
                throw new Error("Server error while processing the download request.");
            } else {
                throw new Error(error.response.data.message || "An unknown error occurred.");
            }
        } else if (error.request) {
            throw new Error("No response received from the server.");
        } else {
            throw new Error("Failed to download folder as ZIP.");
        }
    }
};


//add download file method

// Download a file by ID (user/admin)
export const downloadFileById = async (id: string, fileName: string) => {
    try {
        const response = await baseApi.get(`/document/download/${id}`, { responseType: "blob" });

        if (!response || !response.data) {
            throw new Error("No data received from the server.");
        }

        // Create a Blob and trigger download
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName; // Use provided file name
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        return { message: "File downloaded successfully" };
    } catch (error: any) {
        if (error.response) {
            if (error.response.status === 404) {
                throw new Error("File not found.");
            } else if (error.response.status === 500) {
                throw new Error("Server error while downloading the file.");
            } else {
                throw new Error(error.response.data.message || "An unknown error occurred.");
            }
        } else if (error.request) {
            throw new Error("No response received from the server.");
        } else {
            throw new Error("Failed to download the file.");
        }
    }
};

